import { memo, useState } from 'react';
import type { Card } from '@mtg-deck-tool/shared';
import { getCardImageUri, getCardArtist, isDoubleFaced } from '@mtg-deck-tool/shared';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';
import { useDeckStore } from '../../stores/deck-store.js';

function CardPreviewAreaInner() {
  const { hoveredCard } = useCardPreview();
  const entries = useDeckStore((s) => s.entries);
  const flippedCards = useDeckStore((s) => s.flippedCards);
  const toggleFlip = useDeckStore((s) => s.toggleFlip);
  const [collapsed, setCollapsed] = useState(true);

  const commanders = entries.filter((e) => e.isCommander);
  const defaultCard: Card | undefined = commanders[0]?.card;

  const displayCard = hoveredCard ?? defaultCard ?? null;

  const isFlipped = displayCard ? flippedCards.has(displayCard.oracleId) : false;
  const faceIndex = isFlipped ? 1 : 0;
  const imageUri = displayCard ? getCardImageUri(displayCard, 'normal', faceIndex) : undefined;
  const artist = displayCard
    ? (isFlipped ? (displayCard.cardFaces?.[1]?.artist ?? getCardArtist(displayCard)) : getCardArtist(displayCard))
    : '';

  // On mobile (below lg), collapsed by default with toggle
  if (!displayCard) {
    return (
      <div className="px-4 py-3 border-b border-gray-800 text-center">
        <p className="text-xs text-gray-600 italic">Designate a commander</p>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-800">
      {/* Mobile toggle */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="lg:hidden w-full px-4 py-1.5 text-[10px] text-gray-500 hover:text-gray-300 transition-colors text-center"
      >
        {collapsed ? 'Show card preview' : 'Hide card preview'}
      </button>

      <div className={`${collapsed ? 'hidden' : 'block'} lg:block px-4 pb-3 pt-1`}>
        <div className="flex justify-center relative">
          {imageUri ? (
            <img
              src={imageUri}
              alt={displayCard.name}
              className="rounded-xl shadow-lg border border-gray-700/50 max-h-[340px] w-auto object-contain"
            />
          ) : (
            <div className="w-[244px] h-[340px] bg-gray-800 rounded-xl border border-gray-700/50 flex items-center justify-center text-gray-600 text-xs">
              {displayCard.name}
            </div>
          )}
          {isDoubleFaced(displayCard) && (
            <button
              onClick={() => toggleFlip(displayCard.oracleId)}
              className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-gray-900/80 border border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700 flex items-center justify-center text-sm transition-colors"
              title="Flip card"
            >
              &#x21BB;
            </button>
          )}
        </div>
        <p className="text-center text-[10px] text-gray-600 mt-1.5 truncate min-h-[16px]">
          {artist ? `Art by ${artist}` : '\u00A0'}
        </p>

      </div>
    </div>
  );
}

export const CardPreviewArea = memo(CardPreviewAreaInner);
